// src/data/broadcasts.js
// Mirrors the same technique as src/data/talent.js, but with robust normalization.

const RAW_BASE =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_API_BASE) ||
  '/server/exodia_uno_function';

const BASE = String(RAW_BASE).replace(/\/+$/, '');

const URLS = {
  list:   `${BASE}/broadcasts`,
  add:    `${BASE}/broadcasts`,
  update: (id) => `${BASE}/broadcasts/${encodeURIComponent(id)}`,
  remove: (id) => `${BASE}/broadcasts/${encodeURIComponent(id)}`,
};

async function toJSON(res) {
  const t = await res.text();
  try { return t ? JSON.parse(t) : null; } catch { return t || null; }
}

/** Lowercase + alias common fields so the HomePage renderer always finds title/content/type/date */
function normalizeShape(r) {
  // unwrap common wrappers
  const base = r?.broadcasts || r || {};

  const id =
    base.id ??
    base.ROWID ??
    base.rowid ??
    base.broadcastId ??
    base.broadcast_id ??
    base.uuid ??
    null;

  const title = base.title ?? '';
  const content = base.content ?? '';
  const type = base.type ?? 'Announcement';

  // unify date field names
  const dateRaw =
    base.date ??
    base.datetime ??
    base.createdAt ??
    base.updatedAt ??
    null;

  return { id, title, content, type, date: dateRaw };
}

function sortDescByDate(a, b) {
  const da = new Date(a?.date || 0).getTime();
  const db = new Date(b?.date || 0).getTime();
  return db - da;
}

export const BroadcastsAPI = {
  async list({ q, ...rest } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    Object.entries(rest).forEach(([k, v]) => {
      if (v != null) params.set(k, String(v));
    });

    // cache-buster
    params.set('__t', String(Date.now()));

    const url = `${URLS.list}?${params.toString()}`;
    const res = await fetch(url, { cache: 'no-store' });

    // Some backends may return 304 with an empty body — treat it like empty results
    if (res.status === 304) return [];

    const data = await toJSON(res);
    if (!res.ok) {
      const msg = (data && (data.error?.message || data.error)) || res.statusText;
      throw new Error(msg);
    }

    // Accept many shapes:
    // - [ {...}, {...} ]
    // - { rows: [ {...} ] }
    // - { data: [ {...} ] }
    // - { ok: true, rows: [ { broadcasts: {...} } ] }
    const rowsArray =
      (Array.isArray(data) && data) ||
      data?.rows ||
      data?.data ||
      [];

    const normalized = rowsArray
      .map(normalizeShape)
      // Filter out rows that have neither title nor content so we don’t render blank cards
      .filter(r => (r.title && String(r.title).trim()) || (r.content && String(r.content).trim()))
      .sort(sortDescByDate);

    return normalized;
  },

  async create(body) {
    const res = await fetch(`${URLS.add}?__t=${Date.now()}`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await toJSON(res);
    if (!res.ok) throw new Error(data?.error?.message || data?.error || res.statusText);
    return normalizeShape(data);
  },

  async update(id, patch) {
    const res = await fetch(`${URLS.update(id)}?__t=${Date.now()}`, {
      method: 'PATCH',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    const data = await toJSON(res);
    if (!res.ok) throw new Error(data?.error?.message || data?.error || res.statusText);
    return normalizeShape(data);
  },

  async remove(id) {
    const res = await fetch(`${URLS.remove(id)}?__t=${Date.now()}`, {
      method: 'DELETE',
      cache: 'no-store',
    });
    const data = await toJSON(res);
    if (!res.ok) throw new Error(data?.error?.message || data?.error || res.statusText);
    return data?.ok ? true : data;
  },
};
